import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { groupArtistsByLetter } from '../../util/artistCatalog';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ArtistsPage.module.css';

/**
 * Public A–Z index: signature catalog plus artists from published listings.
 *
 * @component
 */
export const ArtistsPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const { scrollingDisabled, artists = [] } = props;

  const groups = groupArtistsByLetter(artists);
  const marketplaceName = config.marketplaceName || 'Régi Képek';
  const rootUrl = (config.marketplaceRootURL || '').replace(/\/$/, '');

  const title = intl.formatMessage({ id: 'ArtistsPage.schemaTitle' }, { marketplaceName });
  const description = intl.formatMessage(
    { id: 'ArtistsPage.schemaDescription' },
    { marketplaceName, count: artists.length }
  );

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: `${rootUrl}/muveszek`,
    isPartOf: {
      '@type': 'WebSite',
      name: marketplaceName,
      url: rootUrl || undefined,
    },
    mainEntity: {
      '@type': 'ItemList',
      name: intl.formatMessage({ id: 'ArtistsPage.itemListName' }, { marketplaceName }),
      numberOfItems: artists.length,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: artists.map((artist, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: artist.name,
        url: `${rootUrl}/alkoto/${encodeURIComponent(artist.slug)}`,
        item: {
          '@type': 'Person',
          name: artist.name,
          url: `${rootUrl}/alkoto/${encodeURIComponent(artist.slug)}`,
        },
      })),
    },
  };

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled} schema={schema}>
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer currentPage="ArtistsPage" />}
        footer={<FooterContainer />}
      >
        <main className={css.main} id="main-content">
          <header className={css.header}>
            <h1 className={css.heading}>
              <FormattedMessage id="ArtistsPage.heading" />
            </h1>
            <p className={css.intro}>
              <FormattedMessage id="ArtistsPage.intro" />
            </p>
          </header>

          <nav className={css.letterNav} aria-label={intl.formatMessage({ id: 'ArtistsPage.letterNavLabel' })}>
            <ol className={css.letterNavList}>
              {groups.map(({ letter }) => (
                <li key={letter} className={css.letterNavItem}>
                  <a className={css.letterNavLink} href={`#betu-${encodeURIComponent(letter)}`}>
                    {letter}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {groups.map(({ letter, artists: letterArtists }) => (
            <section
              key={letter}
              id={`betu-${letter}`}
              className={css.letterSection}
              aria-labelledby={`heading-betu-${letter}`}
            >
              <h2 id={`heading-betu-${letter}`} className={css.letterHeading}>
                {letter}
              </h2>
              <ul className={css.artistList}>
                {letterArtists.map(artist => (
                  <li key={artist.slug} className={css.artistItem}>
                    <NamedLink
                      name="ArtistPage"
                      params={{ slug: artist.slug }}
                      className={css.artistLink}
                    >
                      {artist.name}
                    </NamedLink>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  artists: state.ArtistsPage?.artists,
});

const ArtistsPage = compose(connect(mapStateToProps))(ArtistsPageComponent);

export default ArtistsPage;
